<script setup lang="ts">
/**
 * 数值控件 —— 复刻 Blender 的交互：
 *   · 在字段上横向拖拽 → 改值（右增左减）
 *   · 单击（无位移）    → 转为文本输入，全选
 *   · Shift 加速 ×10，Ctrl/Cmd 减速 ×0.1
 *   · ↑/↓ 单步增减
 *   · Enter 提交，Esc 放弃
 */
import { computed, nextTick, ref } from 'vue'
import type { NumberWidget } from '../../types/widget'

const props = defineProps<{ widget: NumberWidget }>()
const emit = defineEmits<{ (e: 'update', value: number): void }>()

const inputEl = ref<HTMLInputElement | null>(null)
const dragging = ref(false)
const editing = ref(false)
const draft = ref('')

let startX = 0
let startValue = 0
let moved = false

const w = computed(() => props.widget)
const precision = computed(() => w.value.precision ?? 3)
const hasRange = computed(
  () => typeof w.value.min === 'number' && typeof w.value.max === 'number',
)
/** 每像素基准增量：有值域时按「200px 走完全程」映射，否则用 step */
const baseSensitivity = computed(() =>
  hasRange.value
    ? (w.value.max! - w.value.min!) / 200
    : (w.value.step ?? 0.01),
)

function format(v: number): string {
  if (!Number.isFinite(v)) return '0'
  let s = v.toFixed(precision.value)
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '')
  return s
}

function round(v: number): number {
  const f = 10 ** precision.value
  return Math.round(v * f) / f
}

function clamp(v: number): number {
  if (typeof w.value.min === 'number' && v < w.value.min) return w.value.min
  if (typeof w.value.max === 'number' && v > w.value.max) return w.value.max
  return v
}

function commit(v: number) {
  const next = clamp(round(v))
  if (next !== w.value.value) emit('update', next)
}

function modifierScale(ev: PointerEvent | KeyboardEvent): number {
  let s = 1
  if (ev.shiftKey) s *= 10
  if (ev.ctrlKey || ev.metaKey) s *= 0.1
  return s
}

function onPointerDown(ev: PointerEvent) {
  if (w.value.disabled || ev.button !== 0 || editing.value) return
  // 阻止默认，避免拖拽过程中 input 抢焦点
  ev.preventDefault()
  startX = ev.clientX
  startValue = w.value.value
  moved = false
  dragging.value = true
  ;(ev.currentTarget as HTMLElement).setPointerCapture?.(ev.pointerId)
}

function onPointerMove(ev: PointerEvent) {
  if (!dragging.value) return
  const dx = ev.clientX - startX
  if (!moved && Math.abs(dx) < 3) return // 死区：区分点击与拖拽
  moved = true
  commit(startValue + dx * baseSensitivity.value * modifierScale(ev))
}

function onPointerUp(ev: PointerEvent) {
  if (!dragging.value) return
  dragging.value = false
  ;(ev.currentTarget as HTMLElement).releasePointerCapture?.(ev.pointerId)
  if (!moved) enterEdit()
}

function enterEdit() {
  editing.value = true
  draft.value = format(w.value.value)
  nextTick(() => {
    inputEl.value?.focus()
    inputEl.value?.select()
  })
}

function onFocus() {
  if (!editing.value) {
    editing.value = true
    draft.value = format(w.value.value)
  }
}

function applyDraft() {
  const v = Number.parseFloat(draft.value)
  if (Number.isFinite(v)) commit(v)
}

function onBlur() {
  if (editing.value) applyDraft()
  editing.value = false
}

function onKeydown(ev: KeyboardEvent) {
  if (ev.key === 'Enter') {
    ev.preventDefault()
    applyDraft()
    editing.value = false
    inputEl.value?.blur()
    return
  }
  if (ev.key === 'Escape') {
    ev.preventDefault()
    editing.value = false
    inputEl.value?.blur()
    return
  }
  if (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
    ev.preventDefault()
    const dir = ev.key === 'ArrowUp' ? 1 : -1
    commit(w.value.value + dir * baseSensitivity.value * modifierScale(ev))
  }
}

const displayValue = computed(() =>
  editing.value ? draft.value : format(w.value.value),
)
</script>

<template>
  <div
    class="np-widget np-widget--number nodrag"
    :class="{ 'is-dragging': dragging, 'has-unit': !!w.unit }"
    :title="w.hint"
  >
    <input
      ref="inputEl"
      class="np-widget__field"
      type="text"
      inputmode="decimal"
      autocomplete="off"
      spellcheck="false"
      :disabled="w.disabled"
      :value="displayValue"
      @input="draft = ($event.target as HTMLInputElement).value"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
    />
    <span v-if="w.unit" class="np-widget__unit">{{ w.unit }}</span>
  </div>
</template>
